import NextStopTableScreenScreen from "./NextStopVehicleScreenScreen";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import { useLocation } from "react-router-dom";
import config from "../../config.json";
import axios from "axios";
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import OfflineIndicator from "./OfflineIndicator";
import RouteHeader from "./RouteHeader";
import { updateVehicleDirection } from "../../components/dashboard/dashboardAPI";
import {
  calculateBearing,
  calculateDistance,
} from "../../utils/calculateDistance.js";
import { getRightToLeftBusIcon } from "../stops/vehicleIcons.js";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";

const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];
const libraries = ["places", "directions"];
const POSITION_HISTORY_SIZE = 40;
const DIRECTION_CHECK_THRESHOLD = 8;
const MOVEMENT_THRESHOLD = 10;
const STOP_DETAILS_FETCH_INTERVAL = 500000;
const STAGNATION_CHECK_INTERVAL = 300000;
const OFF_ROUTE_THRESHOLD = 50;
const STAGNATION_DISTANCE_THRESHOLD = 5;

// TODO: TEMPORARY - Remove when proper stop sequencing is implemented
const CHARGING_STATION = {
  lat: 13.19519911007788,
  lng: 77.7063383111108,
};
const P3_PARKING = {
  lat: 13.196487,
  lng: 77.708962,
  stopId: "P3-Parking",
  name: "P3-Parking",
};
const T2_LEVEL_0 = {
  lat: 13.199472,
  lng: 77.715425,
  stopId: "T2-Level 0",
  name: "T2-Level 0",
};
const CHARGING_STATION_PROXIMITY = 100; // meters
const STOP_VISIT_PROXIMITY = 30; // meters

const getStoredUserState = () => {
  try {
    const userStateString = localStorage.getItem("userState");
    if (!userStateString) return null;

    const userState = JSON.parse(userStateString);
    return userState && typeof userState === "object" ? userState : null;
  } catch (error) {
    console.error("Error parsing userState from localStorage:", error);
    return null;
  }
};

const VehicleScreen = () => {
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [position, setPosition] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [speed, setSpeed] = useState(null);
  const [directions, setDirections] = useState(null);
  const [stopDetails, setStopDetails] = useState({});
  const [previousPosition, setPreviousPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [map, setMap] = useState(null);
  const [snappedPosition, setSnappedPosition] = useState(null);
  const [routePaths, setRoutePaths] = useState({ before: [], after: [] });
  const [isMoving, setIsMoving] = useState(true);
  const [lastMovedPosition, setLastMovedPosition] = useState(null);
  const [stoppedDuration, setStoppedDuration] = useState(0);
  const [positionHistory, setPositionHistory] = useState([]);
  const [fullPath, setFullPath] = useState([]);
  const [pathInitialized, setPathInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOffRoute, setIsOffRoute] = useState(false);
  const [offRouteDuration, setOffRouteDuration] = useState(0);
  const [pathDirection, setPathDirection] = useState(null);
  const [lastPosition, setLastPosition] = useState(null);
  const [vehicleStatus, setVehicleStatus] = useState("ACTIVE");
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [hasLeftChargingStation, setHasLeftChargingStation] = useState(false);
  const [chargingStationExitStops, setChargingStationExitStops] = useState([]);
  const locationState =
    location.state && typeof location.state === "object" ? location.state : null;
  const storedUserState = getStoredUserState();
  const storedOrganizationId = storedUserState?.user?.organizationId ?? null;
  const routeStateOrganizationId = locationState?.organizationId ?? null;
  const isMountedRef = useRef(true);
  const directionCheckRef = useRef(false);
  const directionsRef = useRef(directions);
  const eventSourceRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 25;
  const reconnectInterval = 30000;
  const fallbackTimerRef = useRef(null);
  const lastDataReceivedRef = useRef(Date.now());
  const sseHealthTimerRef = useRef(null);
  const lastGpsRef = useRef(null);
  const headingRef = useRef(0);
  const wasAtChargingStationRef = useRef(false);

  const { settings } = useSettings();
  const { getVehicleIcon: getCustomVehicleIcon, getIntermediateStopIcon } =
    useMapIcons(
      settings,
      vehicle ? [vehicle] : [],
      stops,
      route ? [route] : [],
    );

  const mapContainerStyle = {
    width: "100%",
    height: isSmallScreen ? "50vh" : "89vh",
    border: " 1px solid #000000",
    borderRadius: "20px",
  };

  useEffect(() => {
    directionsRef.current = directions;
  }, [directions]);

  const [vehicleId, setVehicleId] = useState(null);
  const [organizationId, setOrganizationId] = useState(
    routeStateOrganizationId ?? storedOrganizationId,
  );
  const resolvedOrganizationId = organizationId ?? storedOrganizationId;

  const vehicleIdMapping = {
    // 6: "291", // shuttle-02
    // 7: "283", //testDevice
    // 8: "287", // shuttle-01
    // 9: "288", // shuttle-03
    // 10: "288", // shuttle-04
    284: "319",
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let vId = urlParams.get("vehicleId");

    if (vId) {
      vId = vehicleIdMapping[vId] || vId;
      setVehicleId(vId);
    } else {
      const pathParts = window.location.pathname.split("/");
      const lastPart = pathParts[pathParts.length - 1];
      if (!isNaN(parseInt(lastPart))) {
        const mappedId = vehicleIdMapping[lastPart] || lastPart;
        setVehicleId(mappedId);
      }
    }

    const orgId = urlParams.get("organizationId");
    if (orgId) {
      const parsedOrgId = parseInt(orgId, 10);
      if (!Number.isNaN(parsedOrgId)) {
        setOrganizationId(parsedOrgId);
      }
    }
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD7rqUpTzUpEbxm-Xc7ikltFJGzOhd92Qk",
    libraries,
  });

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const getRouteById = async (id) => {
    try {
      const resp = await axios.get(`${config.baseURL}/route/id`, {
        params: { id },
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      console.error("Error fetching route:", error);
      return null;
    }
  };

  const fetchStopsForRoute = async (route) => {
    if (!route?.routeCoordinates) return [];

    const stopOrganizationId = route?.organizationId ?? resolvedOrganizationId;
    if (!stopOrganizationId) return [];

    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const stopsResponse = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: stopOrganizationId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          },
        );
        stops.push(stopsResponse.data);
      } catch (error) {
        console.error("Error fetching stops:", error);
      }
    }
    return stops;
  };

  const getVehicleIcon = (heading) => {
    const color = vehicle?.vehicle_color || "green";

    // Use the heading directly without any adjustment
    const rotatedSVG = getRightToLeftBusIcon(color, heading + 90).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(70, 70),
      anchor: new window.google.maps.Point(35, 35),
    };
  };

  useEffect(() => {
    const fetchVehicleData = async () => {
      if (!vehicleId) return;
      try {
        const response = await axios.get(`${config.baseURL}/vehicle/id`, {
          params: {
            vehicleId: vehicleId,
            organizationId: resolvedOrganizationId,
          },
          withCredentials: true,
        });
        const vehicleData = response.data.vehicle;
        if (!resolvedOrganizationId && vehicleData?.organizationId) {
          setOrganizationId(vehicleData.organizationId);
        }
        setVehicle(vehicleData);
        setVehicleStatus(vehicleData.status || "ACTIVE");
        const routeData = await getRouteById(vehicleData.routeId);
        if (routeData) {
          if (!resolvedOrganizationId && routeData?.organizationId) {
            setOrganizationId(routeData.organizationId);
          }
          setRoute(routeData);
          const stopsData = await fetchStopsForRoute(routeData);
          setStops(stopsData);
        }
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
      }
    };
    fetchVehicleData();
  }, [vehicleId, resolvedOrganizationId]);

  const visibleStops = stops.filter((stop) => {
    if (pathDirection === "source_to_dest") {
      return stop.name !== "Manipal Hospital";
    } else if (pathDirection === "dest_to_source") {
      return stop.name !== "BIAL  T1-Departure";
    }
    return true; // Show all stops if pathDirection is null or undefined
  });

  const calculateDistance = useCallback((p1, p2) => {
    if (!p1 || !p2) return Infinity;
    const R = 6371e3;
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
    const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // TODO: TEMPORARY - Helper function for charging station exit sequence
  // Remove when proper stop sequencing is implemented
  const getChargingStationExitNextStop = useCallback(() => {
    if (!hasLeftChargingStation || pathDirection !== "source_to_dest") {
      return null;
    }

    // If P3-Parking hasn't been visited yet, it's the next stop
    if (!chargingStationExitStops.includes(P3_PARKING.stopId)) {
      return P3_PARKING;
    }

    // If P3-Parking visited but T2-Level 0 not, T2 is next
    if (!chargingStationExitStops.includes(T2_LEVEL_0.stopId)) {
      return T2_LEVEL_0;
    }

    // Both visited, return null to use normal routing
    return null;
  }, [hasLeftChargingStation, pathDirection, chargingStationExitStops]);

  const findNearestPointOnRoute = useCallback(
    (position, directions) => {
      if (!position || !directions?.routes?.[0]) return null;
      const route = directions.routes[0];
      let closestPoint = null;
      let minDistance = Infinity;
      let routeIndex = -1;
      let currentRouteIndex = 0;

      route.legs.forEach((leg) => {
        leg.steps.forEach((step) => {
          const path = step.path;
          path.forEach((point, index) => {
            if (index === 0) return;
            const getLat = (p) =>
              typeof p.lat === "function" ? p.lat() : p.lat;
            const getLng = (p) =>
              typeof p.lng === "function" ? p.lng() : p.lng;

            const start = {
              lat: getLat(path[index - 1]),
              lng: getLng(path[index - 1]),
            };
            const end = {
              lat: getLat(point),
              lng: getLng(point),
            };

            const dx = end.lng - start.lng;
            const dy = end.lat - start.lat;
            const segmentLength = Math.sqrt(dx * dx + dy * dy);
            if (segmentLength === 0) return;

            const t =
              ((position.lng - start.lng) * dx +
                (position.lat - start.lat) * dy) /
              (segmentLength * segmentLength);

            if (t < 0) {
              const distToStart = calculateDistance(position, start);
              if (distToStart < minDistance) {
                minDistance = distToStart;
                closestPoint = start;
                routeIndex = currentRouteIndex;
              }
              return;
            }

            if (t > 1) {
              const distToEnd = calculateDistance(position, end);
              if (distToEnd < minDistance) {
                minDistance = distToEnd;
                closestPoint = end;
                routeIndex = currentRouteIndex + 1;
              }
              return;
            }

            const projectedPoint = {
              lat: start.lat + t * dy,
              lng: start.lng + t * dx,
            };
            const distance = calculateDistance(position, projectedPoint);
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = projectedPoint;
              routeIndex = currentRouteIndex + t;
            }

            currentRouteIndex++;
          });
        });
      });

      const MAX_SNAP_DISTANCE = 50;
      if (minDistance <= MAX_SNAP_DISTANCE) {
        return {
          ...closestPoint,
          routeIndex: routeIndex,
        };
      }
      return null;
    },
    [calculateDistance],
  );

  const detectVehicleMovement = useCallback(
    (newPosition, lastPosition) => {
      if (!lastPosition || !newPosition) return true;
      const distance = calculateDistance(newPosition, lastPosition);
      return distance > MOVEMENT_THRESHOLD;
    },
    [calculateDistance],
  );

  const extractPathPoints = useCallback((directions) => {
    if (!directions?.routes?.[0]?.legs) return [];
    let points = [];
    directions.routes[0].legs.forEach((leg) => {
      leg.steps.forEach((step) => {
        const path = step.path;
        path.forEach((point) => {
          // Fix here: safely extract lat/lng whatever the type
          points.push({
            lat: typeof point.lat === "function" ? point.lat() : point.lat,
            lng: typeof point.lng === "function" ? point.lng() : point.lng,
          });
        });
      });
    });
    return points;
  }, []);

  const isJourneyComplete = useCallback(
    (currentPosition) => {
      if (!stops || stops.length === 0 || !currentPosition || !pathDirection) {
        return false;
      }
      const finalStop =
        pathDirection === "source_to_dest" ? stops[stops.length - 1] : stops[0];
      const finalStopPosition = {
        lat: parseFloat(finalStop.latitude),
        lng: parseFloat(finalStop.longitude),
      };
      const distanceToFinal = calculateDistance(
        currentPosition,
        finalStopPosition,
      );
      const TERMINAL_RADIUS = 50;
      const routeCompletionRatio =
        routePaths.before.length /
        (routePaths.before.length + routePaths.after.length);
      let isNearTerminalSection = false;
      if (fullPath.length > 0) {
        const terminalSectionStartIdx = Math.floor(fullPath.length * 0.9);
        const terminalSection = fullPath.slice(terminalSectionStartIdx);
        let minDistanceToTerminalSection = Infinity;
        terminalSection.forEach((point) => {
          const distance = calculateDistance(currentPosition, point);
          minDistanceToTerminalSection = Math.min(
            minDistanceToTerminalSection,
            distance,
          );
        });
        isNearTerminalSection = minDistanceToTerminalSection < TERMINAL_RADIUS;
      }
      const isCloseToFinalStop = distanceToFinal < TERMINAL_RADIUS;
      const hasCompletedMostOfRoute = routeCompletionRatio > 0.95;
      const isStoppedNearEnd =
        !isMoving &&
        (isCloseToFinalStop || isNearTerminalSection) &&
        stoppedDuration > 20;
      const isComplete =
        (isCloseToFinalStop && hasCompletedMostOfRoute) ||
        (isNearTerminalSection && hasCompletedMostOfRoute) ||
        isStoppedNearEnd;
      return isComplete;
    },
    [
      stops,
      pathDirection,
      calculateDistance,
      routePaths.before,
      routePaths.after,
      fullPath,
      isMoving,
      stoppedDuration,
    ],
  );

  const checkDirectionChange = useCallback(
    (positions) => {
      if (
        positions.length < DIRECTION_CHECK_THRESHOLD ||
        !stops ||
        stops.length < 2
      ) {
        return null;
      }
      const source = {
        lat: parseFloat(stops[0].latitude),
        lng: parseFloat(stops[0].longitude),
      };
      const destination = {
        lat: parseFloat(stops[stops.length - 1].latitude),
        lng: parseFloat(stops[stops.length - 1].longitude),
      };
      const firstPos = positions[0];
      const lastPos = positions[positions.length - 1];
      const firstPosToSource = calculateDistance(firstPos, source);
      const firstPosToDest = calculateDistance(firstPos, destination);
      const lastPosToSource = calculateDistance(lastPos, source);
      const lastPosToDest = calculateDistance(lastPos, destination);
      const sourceDistanceChange = lastPosToSource - firstPosToSource;
      const destDistanceChange = lastPosToDest - firstPosToDest;

      if (
        Math.abs(sourceDistanceChange) > 10 ||
        Math.abs(destDistanceChange) > 10
      ) {
        if (sourceDistanceChange < 0 && destDistanceChange > 0) {
          return "dest_to_source";
        } else if (sourceDistanceChange > 0 && destDistanceChange < 0) {
          return "source_to_dest";
        }
      }
      return null;
    },
    [stops, calculateDistance],
  );

  const splitRouteAtPosition = useCallback(
    (allPoints, position) => {
      if (!allPoints.length || !position) {
        return { before: [], after: [] };
      }
      if (!pathDirection) {
        return { before: allPoints, after: [] };
      }
      let splitIndex = 0;
      let minDistance = Infinity;
      allPoints.forEach((point, index) => {
        const distance = calculateDistance(position, point);
        if (distance < minDistance) {
          minDistance = distance;
          splitIndex = index;
        }
      });
      if (pathDirection === "source_to_dest") {
        return {
          before: allPoints.slice(0, splitIndex + 1),
          after: allPoints.slice(splitIndex),
        };
      } else {
        return {
          before: allPoints.slice(splitIndex),
          after: allPoints.slice(0, splitIndex + 1),
        };
      }
    },
    [pathDirection, calculateDistance],
  );

  const fetchLocation = useCallback(async () => {
    if (!vehicle?.device_id || eventSourceRef.current) return;

    // Initial database fetch
    const fetchInitialPosition = async () => {
      try {
        const response = await fetch(
          `${config.baseURL}/location/latest/${vehicle.device_id}`,
        );
        const data = await response.json();
        if (!isMountedRef.current) return;

        if (data.latitude && data.longitude) {
          const newPosition = {
            lat: parseFloat(data.latitude),
            lng: parseFloat(data.longitude),
            timestamp: Date.now(),
          };
          console.log("Initial DB position:", newPosition);
          processLocationUpdate(newPosition, data);
        }
      } catch (error) {
        console.error("Error fetching initial DB position:", error);
        setIsOnline(false);
      }
    };

    const processLocationUpdate = (newPosition, locationData) => {
      setIsOnline(true);
      setIsReconnecting(false);
      reconnectAttemptsRef.current = 0;

      // ========================================
      // 1. CALCULATE HEADING FIRST (before any position updates)
      // ========================================
      if (lastGpsRef.current) {
        const prevPos = lastGpsRef.current;
        const currPos = newPosition;

        // Calculate distance moved
        const distMoved = calculateDistance(prevPos, currPos);

        // Only update heading if vehicle has moved significantly
        if (distMoved > 5) {
          // 5 meters threshold
          // Calculate bearing from PREVIOUS to CURRENT position
          const bearing = calculateBearing(
            { latitude: prevPos.lat, longitude: prevPos.lng }, // FROM (previous)
            { latitude: currPos.lat, longitude: currPos.lng }, // TO (current)
          );
          headingRef.current = bearing;
          setHeading(bearing);
          console.log(
            "Updated heading:",
            bearing,
            "Distance moved:",
            distMoved,
          );
        } else {
          // console.log(
          //   "Movement too small, keeping previous heading:",
          //   headingRef.current
          // );
        }
      } else {
        // Initialize heading on first position
        headingRef.current = 0;
        setHeading(0);
      }

      // Update last GPS position for next iteration (AFTER calculating bearing)
      lastGpsRef.current = { lat: newPosition.lat, lng: newPosition.lng };

      // ========================================
      // 2. OFF-ROUTE CHECK
      // ========================================
      let isCurrentlyOffRoute = false;
      if (directionsRef.current) {
        const snapped = findNearestPointOnRoute(
          newPosition,
          directionsRef.current,
        );
        const distanceFromRoute = snapped
          ? calculateDistance(newPosition, snapped)
          : Infinity;
        isCurrentlyOffRoute = distanceFromRoute > OFF_ROUTE_THRESHOLD;
      }

      if (isCurrentlyOffRoute) {
        setIsOffRoute(true);
        setOffRouteDuration((prev) => prev + 10);
        if (offRouteDuration >= 500 && vehicle?.device_id) {
          localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
          setPathDirection(null);
          setPositionHistory([]);
          console.log("Cleared pathDirection due to long off-route stop");
        }
      } else {
        setIsOffRoute(false);
        setOffRouteDuration(0);
      }

      // ========================================
      // 3.1 CHARGING STATION FORCE CHECK & EXIT TRACKING
      // ========================================
      const distToChargingStation = calculateDistance(
        newPosition,
        CHARGING_STATION,
      );

      // console.log(
      //   `📍 Distance to Charging Station: ${distToChargingStation.toFixed(2)}m`
      // );

      // Track if vehicle is currently at charging station
      const isAtChargingStation =
        distToChargingStation <= CHARGING_STATION_PROXIMITY;

      if (isAtChargingStation) {
        wasAtChargingStationRef.current = true;
        // Force direction to source_to_dest
        if (pathDirection !== "source_to_dest") {
          console.log(
            `🔌 Vehicle is near Charging Station (<= ${CHARGING_STATION_PROXIMITY}m) - Forcing direction to source_to_dest`,
          );
          setPathDirection("source_to_dest");
          if (vehicle?.device_id) {
            try {
              localStorage.setItem(
                `pathDirection_${vehicle.device_id}`,
                JSON.stringify({
                  direction: "source_to_dest",
                  timestamp: Date.now(),
                  vehicleId: vehicle.device_id,
                }),
              );
            } catch (error) {
              console.error("Failed to save to localStorage:", error);
            }
          }
        }
        // Reset exit tracking when at charging station
        setHasLeftChargingStation(false);
        setChargingStationExitStops([]);
      } else {
        // console.log(
        //   `🚗 Vehicle is NOT near Charging Station (${distToChargingStation.toFixed(2)}m away) - Proceeding with normal direction finding logic`
        // );

        // Detect when vehicle has left charging station
        if (wasAtChargingStationRef.current && !hasLeftChargingStation) {
          // console.log("🚀 Vehicle has LEFT the Charging Station - Starting hardcoded stop sequence");
          setHasLeftChargingStation(true);
          wasAtChargingStationRef.current = false;
        }
      }

      // ========================================
      // 3.2 HARDCODED CHARGING STATION EXIT STOP SEQUENCE TRACKING
      // TODO: TEMPORARY - Remove when proper stop sequencing is implemented
      // ========================================
      if (hasLeftChargingStation && pathDirection === "source_to_dest") {
        // Check proximity to P3-Parking
        const distToP3 = calculateDistance(newPosition, P3_PARKING);
        if (
          distToP3 <= STOP_VISIT_PROXIMITY &&
          !chargingStationExitStops.includes(P3_PARKING.stopId)
        ) {
          // console.log(`✅ Vehicle visited ${P3_PARKING.name} (distance: ${distToP3.toFixed(2)}m)`);
          setChargingStationExitStops((prev) => [...prev, P3_PARKING.stopId]);
        }

        // Check proximity to T2-Level 0
        const distToT2 = calculateDistance(newPosition, T2_LEVEL_0);
        if (
          distToT2 <= STOP_VISIT_PROXIMITY &&
          !chargingStationExitStops.includes(T2_LEVEL_0.stopId)
        ) {
          console.log(
            `✅ Vehicle visited ${T2_LEVEL_0.name} (distance: ${distToT2.toFixed(2)}m)`,
          );
          setChargingStationExitStops((prev) => [...prev, T2_LEVEL_0.stopId]);

          // After visiting both stops, reset the tracking
          if (chargingStationExitStops.includes(P3_PARKING.stopId)) {
            console.log(
              "✅ Completed charging station exit sequence - Returning to normal routing",
            );
            setHasLeftChargingStation(false);
          }
        }
      }

      // ========================================
      // 4. POSITION HISTORY & MOVEMENT
      // ========================================
      if (
        !isCurrentlyOffRoute &&
        detectVehicleMovement(newPosition, lastMovedPosition)
      ) {
        setPositionHistory((prev) => {
          const updated = [...prev, newPosition].slice(-POSITION_HISTORY_SIZE);
          return updated;
        });
      }

      const speed = parseFloat(locationData?.Speed) || 0;
      setSpeed(speed);
      const moving = detectVehicleMovement(newPosition, lastMovedPosition);

      if (moving) {
        setIsMoving(true);
        setLastMovedPosition(newPosition);
        setStoppedDuration(0);
      } else {
        setIsMoving(false);
        setStoppedDuration((prev) => prev + 10);
      }

      // ========================================
      // 5. POSITION SNAPPING & UPDATE
      // ========================================
      if (directionsRef.current && !isCurrentlyOffRoute) {
        const snapped = findNearestPointOnRoute(
          newPosition,
          directionsRef.current,
        );
        if (snapped) {
          setPreviousPosition(position);
          setSnappedPosition(snapped);
          setPosition({
            ...newPosition,
            lat: snapped.lat,
            lng: snapped.lng,
          });
        } else if (
          position &&
          calculateDistance(position, newPosition) > MOVEMENT_THRESHOLD * 2
        ) {
          setPreviousPosition(position);
          setPosition(newPosition);
        }
      } else {
        setPosition(newPosition);
      }

      setLastPosition(newPosition);

      // ========================================
      // 6. MAP CENTERING
      // ========================================
      if (map) {
        map.panTo(newPosition);
      }
    };

    // Database fallback function
    const fetchFromDatabase = async () => {
      try {
        const response = await fetch(
          `${config.baseURL}/location/latest/${vehicle.device_id}`,
        );
        const data = await response.json();

        if (!isMountedRef.current) return;

        const newPosition = {
          lat: parseFloat(data?.latitude),
          lng: parseFloat(data?.longitude),
          timestamp: Date.now(),
        };

        if (isNaN(newPosition.lat) || isNaN(newPosition.lng)) {
          console.warn("Invalid coordinates from database");
          return;
        }

        // console.log("Database fallback data:", newPosition);
        processLocationUpdate(newPosition, data);
      } catch (error) {
        console.error("Error fetching from database:", error);
      }
    };

    // Start database fallback
    const startDatabaseFallback = () => {
      if (fallbackTimerRef.current) return;

      // console.log("Starting database fallback");
      fetchFromDatabase();

      fallbackTimerRef.current = setInterval(() => {
        if (isMountedRef.current && !eventSourceRef.current) {
          fetchFromDatabase();
        }
      }, 10000);
    };

    // Stop database fallback
    const stopDatabaseFallback = () => {
      if (fallbackTimerRef.current) {
        clearInterval(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
        // console.log("Stopped database fallback");
      }
    };

    // SSE connection function
    const connectSSE = () => {
      if (eventSourceRef.current) {
        console.log("SSE already connected, skipping new connection");
        return;
      }

      const query = `vehicleId=${vehicle.device_id}`;
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;
      lastDataReceivedRef.current = Date.now();

      eventSource.onmessage = (event) => {
        try {
          const locationData = JSON.parse(event.data);
          // console.log("SSE Data Received:", locationData);
          lastDataReceivedRef.current = Date.now();

          if (!isMountedRef.current) return;

          const newPosition = {
            lat: parseFloat(locationData?.Latitude || locationData?.latitude),
            lng: parseFloat(locationData?.Longitude || locationData?.longitude),
            timestamp: Date.now(),
          };

          if (!newPosition.lat || !newPosition.lng) {
            console.error("Invalid position data:", newPosition);
            return;
          }

          stopDatabaseFallback();
          processLocationUpdate(newPosition, locationData);
        } catch (error) {
          console.error("Error processing SSE data:", error);
        }
      };

      eventSource.onerror = () => {
        setIsOnline(false);
        setIsReconnecting(true);
        eventSource.close();
        eventSourceRef.current = null;

        startDatabaseFallback();

        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          // console.log(
          //   `Reconnecting SSE, attempt ${reconnectAttemptsRef.current}`
          // );
          setTimeout(connectSSE, reconnectInterval);
        } else {
          console.error("Max reconnect attempts reached");
          setIsReconnecting(false);
        }
      };

      const healthCheck = setInterval(() => {
        if (Date.now() - lastDataReceivedRef.current > 180000) {
          console.warn(
            "No SSE data for 180 seconds, starting database fallback",
          );
          startDatabaseFallback();
        }
      }, 15000);

      sseHealthTimerRef.current = healthCheck;
    };

    // Fetch initial position before SSE
    await fetchInitialPosition();
    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        // console.log("Closed SSE connection");
      }
      stopDatabaseFallback();
      if (sseHealthTimerRef.current) {
        clearInterval(sseHealthTimerRef.current);
        sseHealthTimerRef.current = null;
        // console.log("Cleared SSE health check timer");
      }
    };
  }, [vehicle?.device_id, vehicleId, resolvedOrganizationId]);

  useEffect(() => {
    if (!vehicle?.device_id) return;
    isMountedRef.current = true;
    const cleanup = fetchLocation();
    return () => {
      isMountedRef.current = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [vehicle?.device_id, fetchLocation]);

  useEffect(() => {
    if (!vehicle?.device_id) return;

    const checkStagnation = async () => {
      try {
        const locationResponse = await fetch(
          `${config.baseURL}/location/latest/${vehicle.device_id}`,
        );
        const locationData = await locationResponse.json();
        const currentPosition = {
          lat: parseFloat(locationData?.latitude),
          lng: parseFloat(locationData?.longitude),
        };

        const vehicleResponse = await axios.get(
          `${config.baseURL}/vehicle/id`,
          {
            params: {
              vehicleId: vehicleId,
              organizationId: resolvedOrganizationId,
            },
            withCredentials: true,
          },
        );
        const currentStatus = vehicleResponse.data.vehicle.status || "ACTIVE";

        if (
          lastPosition &&
          calculateDistance(currentPosition, lastPosition) <=
            STAGNATION_DISTANCE_THRESHOLD &&
          ["INACTIVE", "DEAD"].includes(currentStatus)
        ) {
          console.log(
            `Resetting pathDirection due to stagnation (status: ${currentStatus})`,
          );
          setPathDirection(null);
          setPositionHistory([]);
          localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
        }

        setLastPosition(currentPosition);
        setVehicleStatus(currentStatus);
      } catch (error) {
        console.error("Error during stagnation check:", error);
      }
    };

    const intervalId = setInterval(checkStagnation, STAGNATION_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [
    vehicle?.device_id,
    vehicleId,
    resolvedOrganizationId,
    lastPosition,
    calculateDistance,
  ]);

  useEffect(() => {
    if (!isLoaded || !stops?.length || !route?.routeCoordinates) return;

    // 1️⃣ Generate new cache key (route id + coordinates)
    const cacheKey = `directions_${route.id}_${stops
      .map((s) => `${s.stopId}_${s.latitude}_${s.longitude}`)
      .join("-")}`;

    // console.log("🧠 [Directions] Cache Key:", cacheKey);

    // 2️⃣ Clean up old cache keys for same route.id
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`directions_${route.id}_`) && key !== cacheKey) {
        console.log(`🗑️ [Directions] Removing old cache entry: ${key}`);
        localStorage.removeItem(key);
      }
    });

    // 3️⃣ Check if current key exists
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          // console.log("✅ [Directions] Using cached directions");
          setDirections(data);
          const allPoints = extractPathPoints(data);
          setFullPath(allPoints);
          if (!position && map) {
            map.setCenter(
              new window.google.maps.LatLng(
                parseFloat(stops[0].latitude),
                parseFloat(stops[0].longitude),
              ),
            );
          }
          return; // ⏹ Skip API call
        } else {
          // console.log("⌛ [Directions] Cache expired");
        }
      } catch (e) {
        console.warn("⚠️ [Directions] Cache corrupted, clearing...");
        localStorage.removeItem(cacheKey);
      }
    } else {
      // console.log("📭 [Directions] No cache found, making API call");
    }

    // 4️⃣ Fetch new directions from API
    const directionsService = new window.google.maps.DirectionsService();
    const waypoints = stops.map((stop) => ({
      location: new window.google.maps.LatLng(
        parseFloat(stop.latitude),
        parseFloat(stop.longitude),
      ),
      stopover: true,
    }));

    const request = {
      origin: waypoints[0].location,
      destination: waypoints[waypoints.length - 1].location,
      waypoints: waypoints.slice(1, -1),
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };

    console.log("🚀 [Directions] Requesting from API...");
    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        // console.log("✅ [Directions] API success, caching new data");
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            data: result,
            timestamp: Date.now(),
          }),
        );
        setDirections(result);
        const allPoints = extractPathPoints(result);
        setFullPath(allPoints);
        if (!position && map) {
          map.setCenter(waypoints[0].location);
        }
      } else {
        console.error("❌ [Directions] API failed:", status);
      }
    });
  }, [stops, route, isLoaded, position, map, extractPathPoints]);

  useEffect(() => {
    if (
      positionHistory.length < POSITION_HISTORY_SIZE ||
      !isMoving ||
      isOffRoute ||
      !stops ||
      stops.length < 2
    ) {
      return;
    }

    const source = {
      lat: parseFloat(stops[0].latitude),
      lng: parseFloat(stops[0].longitude),
    };
    const destination = {
      lat: parseFloat(stops[stops.length - 1].latitude),
      lng: parseFloat(stops[stops.length - 1].longitude),
    };

    let sourceDistanceChange = 0;
    let destDistanceChange = 0;
    for (let i = 1; i < positionHistory.length; i++) {
      const prevPos = positionHistory[i - 1];
      const currPos = positionHistory[i];
      sourceDistanceChange +=
        calculateDistance(currPos, source) - calculateDistance(prevPos, source);
      destDistanceChange +=
        calculateDistance(currPos, destination) -
        calculateDistance(prevPos, destination);
    }

    let newDirection = null;
    if (sourceDistanceChange < 0 && destDistanceChange > 0) {
      newDirection = "dest_to_source";
    } else if (sourceDistanceChange > 0 && destDistanceChange < 0) {
      newDirection = "source_to_dest";
    }

    if (newDirection && newDirection !== pathDirection && vehicle?.device_id) {
      console.log(
        `Updating pathDirection to ${newDirection} based on 15-point trend`,
      );
      setPathDirection(newDirection);
      try {
        localStorage.setItem(
          `pathDirection_${vehicle.device_id}`,
          JSON.stringify({
            direction: newDirection,
            timestamp: Date.now(),
            vehicleId: vehicle.device_id,
          }),
        );
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }
    }

    setPositionHistory((prev) => prev.slice(-DIRECTION_CHECK_THRESHOLD));
  }, [
    positionHistory,
    isMoving,
    isOffRoute,
    stops,
    pathDirection,
    vehicle?.device_id,
    calculateDistance,
  ]);

  useEffect(() => {
    if (
      positionHistory.length < DIRECTION_CHECK_THRESHOLD ||
      directionCheckRef.current ||
      !isMoving ||
      isOffRoute
    ) {
      return;
    }

    directionCheckRef.current = true;

    const currentPosition = positionHistory[positionHistory.length - 1];

    // In the direction checking effect (around lines 873-880)
    // Replace NULL with REVERSE direction
    if (pathDirection && isJourneyComplete(currentPosition)) {
      console.log("Journey completed, reversing direction");

      // Reverse the direction instead of nullifying
      const newDirection =
        pathDirection === "source_to_dest"
          ? "dest_to_source"
          : "source_to_dest";

      setPathDirection(newDirection);
      setPositionHistory([]); // Clear history for fresh direction detection

      if (vehicle?.device_id) {
        localStorage.setItem(
          `pathDirection_${vehicle.device_id}`,
          JSON.stringify({
            direction: newDirection,
            timestamp: Date.now(),
            vehicleId: vehicle.device_id,
          }),
        );
      }

      // console.log(`Direction reversed from ${pathDirection} to ${newDirection}`);
    } else if (!pathDirection) {
      const newDirection = checkDirectionChange(positionHistory);
      if (newDirection && vehicle?.device_id) {
        // console.log(`Setting initial direction: ${newDirection}`);
        setPathDirection(newDirection);
        try {
          localStorage.setItem(
            `pathDirection_${vehicle.device_id}`,
            JSON.stringify({
              direction: newDirection,
              timestamp: Date.now(),
              vehicleId: vehicle.device_id,
            }),
          );
        } catch (error) {
          console.error("Failed to save to localStorage:", error);
        }
      }
    }

    setTimeout(() => {
      directionCheckRef.current = false;
    }, 5000);
  }, [
    positionHistory,
    pathDirection,
    vehicle?.device_id,
    isJourneyComplete,
    checkDirectionChange,
    isMoving,
    isOffRoute,
  ]);

  useEffect(() => {
    if (directions && snappedPosition) {
      const allPoints = extractPathPoints(directions);
      const split = splitRouteAtPosition(allPoints, snappedPosition);
      setRoutePaths(split);
      // console.log("Route Paths Updated:", {
      //   beforeLength: split.before.length,
      //   afterLength: split.after.length,
      // }); // Debug log
    }
  }, [directions, snappedPosition, extractPathPoints, splitRouteAtPosition]);

  useEffect(() => {
    if (!directions || !position || snappedPosition) return;

    const snapped = findNearestPointOnRoute(position, directions);
    if (snapped) {
      setSnappedPosition(snapped);
    }
  }, [directions, position, snappedPosition, findNearestPointOnRoute]);

  const fetchStopDetails = async (stop, index, totalStops) => {
    if (!vehicle?.device_id) return { distance: "N/A", eta: "N/A" };
    try {
      const response = await fetch(`${config.baseURL}/distance/time/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicleId: vehicle.device_id,
          longitude: stop.longitude,
          latitude: stop.latitude,
          isExtreamPoint: index === 0 || index === totalStops - 1,
        }),
      });
      if (!response.ok) {
        throw new Error(`Distance/Time API error: ${response.status}`);
      }
      const data = await response.json();
      return {
        distance: data.distance || "N/A",
        eta: data.eta || "N/A",
      };
    } catch (error) {
      console.error(`Error fetching details for stop:`, error);
      return { distance: "N/A", eta: "N/A" };
    }
  };

  useEffect(() => {
    if (!stops?.length || !vehicle) return;
    const updateStopDetails = async () => {
      const details = {};
      for (let i = 0; i < stops.length; i++) {
        const stopData = await fetchStopDetails(stops[i], i, stops.length);
        details[stops[i].stopId] = stopData;
      }
      setStopDetails(details);
    };
    updateStopDetails();
    const intervalId = setInterval(
      updateStopDetails,
      STOP_DETAILS_FETCH_INTERVAL,
    );
    return () => clearInterval(intervalId);
  }, [stops, vehicle]);

  useEffect(() => {
    return () => {
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
      setPathDirection(null);
      setPathInitialized(false);
      setPositionHistory([]);
    };
  }, [vehicle?.device_id]);

  const prevDirectionRef = useRef(null);

  useEffect(() => {
    if (!vehicle?.device_id || !pathDirection) return;
    if (prevDirectionRef.current === pathDirection) return;

    prevDirectionRef.current = pathDirection;

    updateVehicleDirection(vehicle.device_id, pathDirection).catch((err) => {
      console.error("Failed to update direction:", err);
    });
  }, [pathDirection, vehicle?.device_id]);

  if (loadError) {
    return (
      <div>
        <TitleBar />
        <NavMenu />
        <div style={{ padding: "32px" }}>
          Error loading maps. Please try again later.
        </div>
      </div>
    );
  }
  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: isSmallScreen ? "column-reverse" : "row-reverse",
          gap: isSmallScreen ? "10px" : "4px",
          width: "100%",
          height: "100%",
        }}
      >
        <>
          {!route || !stops ? (
            <div style={{ marginBottom: "20px" }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {/* {console.log("🎯 Rendering NextStopTableScreenScreen with:", {
                pathDirection,
                isInbound: pathDirection === "dest_to_source",
                visibleStopsCount: visibleStops.length,
              })} */}
              <NextStopTableScreenScreen
                details={route}
                stops={visibleStops}
                stopDetails={stopDetails}
                isInbound={pathDirection === "dest_to_source"}
                currentPosition={snappedPosition ?? position}
                routeCoordinates={route?.routeCoordinates}
                onUTurnDetected={() => {
                  // Flip direction
                  const newDirection =
                    pathDirection === "source_to_dest"
                      ? "dest_to_source"
                      : "source_to_dest";

                  // console.log(
                  //   "🔄 U-TURN detected — reversing pathDirection:",
                  //   newDirection
                  // );

                  setPathDirection(newDirection);

                  // Persist to localStorage
                  if (vehicle?.device_id) {
                    localStorage.setItem(
                      `pathDirection_${vehicle.device_id}`,
                      JSON.stringify({
                        direction: newDirection,
                        timestamp: Date.now(),
                        vehicleId: vehicle.device_id,
                      }),
                    );
                  }

                  // Optional: update backend
                  updateVehicleDirection(vehicle.device_id, newDirection).catch(
                    (err) => console.error("Failed to update direction:", err),
                  );
                }}
              />
            </>
          )}
          {!isOnline && <OfflineIndicator />}
          {!isLoaded ? (
            <div
              style={{
                height: "68.5vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div
              style={{
                width: isSmallScreen ? "100%" : "73vw",
                height: isSmallScreen ? "50vh" : "98vh",
                position: "relative",
              }}
            >
              {route && stops && (
                <RouteHeader
                  stops={stops}
                  route={route}
                  pathDirection={pathDirection}
                />
              )}
              <div
                style={{
                  position: "absolute",
                  padding: "0px 16px",
                  top: isSmallScreen ? "15%" : "10%",
                  left: "0",
                  width: "100%",
                  height: isSmallScreen ? "85%" : "90%",
                }}
              >
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={
                    position ||
                    (stops?.length
                      ? {
                          lat: parseFloat(stops[0].latitude),
                          lng: parseFloat(stops[0].longitude),
                        }
                      : null)
                  }
                  zoom={16}
                  onLoad={onLoad}
                  onUnmount={onUnmount}
                  options={{
                    styles: mapStyles,
                    disableDefaultUI: true,
                    zoomControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    scrollwheel: false,
                    draggable: false,
                    gestureHandling: "none",
                    keyboardShortcuts: false,
                    minZoom: 16.5,
                    maxZoom: 30,
                    animatedZoom: true,
                    maxZoomRectangleSize: 8000,
                  }}
                >
                  {!pathDirection && fullPath.length > 0 && (
                    <Polyline
                      path={fullPath}
                      options={{
                        strokeColor: "#808080",
                        strokeOpacity: 1.0,
                        strokeWeight: 10,
                      }}
                    />
                  )}
                  {pathDirection && fullPath.length > 0 && (
                    <>
                      {routePaths.before.length > 0 && (
                        <Polyline
                          path={routePaths.before}
                          options={{
                            strokeColor: "#808080",
                            strokeOpacity: 1.0,
                            strokeWeight: 6,
                          }}
                        />
                      )}
                      {routePaths.after.length > 0 && (
                        <Polyline
                          path={routePaths.after}
                          options={{
                            strokeColor: route?.routeColor || "#2196F3",
                            strokeOpacity: 1.0,
                            strokeWeight: 10,
                          }}
                        />
                      )}
                    </>
                  )}
                  {!isMoving && stoppedDuration > 60 && position && (
                    <Marker
                      position={position}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 16,
                        fillColor: "#FF0000",
                        fillOpacity: 0.7,
                        strokeWeight: 2,
                        strokeColor: "#FFFFFF",
                      }}
                      title={`Vehicle stopped for ${Math.floor(
                        stoppedDuration / 60,
                      )} minutes`}
                    />
                  )}
                  {visibleStops.map((stop, index) =>
                    stop.name && stop.name.trim() !== "" ? (
                      <React.Fragment key={`stop-${stop.id}-${index}`}>
                        <OverlayView
                          position={{
                            lat: parseFloat(stop.latitude),
                            lng: parseFloat(stop.longitude),
                          }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <div
                            style={{
                              position: "relative",
                              top: "-182px",
                              left: "0px",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                width: "2px",
                                backgroundColor: "blue",
                                height: "150px",
                                top: "10px",
                                left: "50%",
                                transform: "translateX(-50%)",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "white",
                                color:
                                  index === 0
                                    ? "red"
                                    : index === stops.length - 1
                                      ? "blue"
                                      : "black",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "24px",
                                fontWeight: "500",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                whiteSpace: "nowrap",
                                zIndex: 1000,
                              }}
                            >
                              {stop.name}
                            </div>
                          </div>
                        </OverlayView>
                        <Marker
                          key={`marker-${stop.id}-${index}`}
                          position={{
                            lat: parseFloat(stop.latitude),
                            lng: parseFloat(stop.longitude),
                          }}
                          icon={getIntermediateStopIcon() || undefined}
                        />
                      </React.Fragment>
                    ) : null,
                  )}

                  {position &&
                    isFinite(position.lat) &&
                    isFinite(position.lng) && (
                      <>
                        {/* Vehicle Name Label */}
                        <OverlayView
                          position={position}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <div
                            style={{
                              position: "absolute",
                              bottom: "60px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#183059",
                              color: "white",
                              padding: "10px 16px",
                              borderRadius: "8px",
                              fontSize: "22px",
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              border: "1px solid black",
                              pointerEvents: "none",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                            }}
                          >
                            {vehicle?.name || "Vehicle"}
                          </div>
                        </OverlayView>

                        {/* CUSTOM ROTATING VEHICLE ICON */}
                        <Marker
                          position={position}
                          optimized={false}
                          icon={
                            getCustomVehicleIcon(
                              vehicle?.device_id,
                              headingRef.current || 0,
                              pathDirection,
                            ) || getVehicleIcon(headingRef.current)
                          }
                          zIndex={2000}
                        />
                      </>
                    )}
                  {/* ROTATING VEHICLE MARKER
                        <Marker
                          position={position}
                          optimized={false}
                          icon={{
                            path: window.google.maps.SymbolPath
                              .FORWARD_CLOSED_ARROW,
                            scale: 10,
                            fillColor: vehicle?.vehicle_color || "#00AA00",
                            fillOpacity: 1,
                            strokeWeight: 2,
                            strokeColor: "#FFFFFF",
                            // Use the raw heading without any pathDirection adjustment
                            rotation: headingRef.current,
                            anchor: new window.google.maps.Point(0, 2),
                          }}
                          zIndex={2000}
                        /> */}
                </GoogleMap>
              </div>
            </div>
          )}
        </>
      </div>
    </>
  );
};

export default VehicleScreen;
